import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import courseraPlusProductVariants from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { constants } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import type { PropsFromWithExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import { getProductFromContentful } from 'bundles/coursera-plus-landing-page/contentful/constants';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { shouldGetFreeTrials } from 'bundles/coursera-plus-landing-page/contentfulData/LandingPageDataProvider';
import type {
  PropsFromPromotion,
  PropsToLandingPageProvider,
  SubscriptionTiersProductPrice,
} from 'bundles/coursera-plus-landing-page/contentfulData/LandingPageDataProvider';
import { useNaptime } from 'bundles/naptimejs';
import ProductPricesV3 from 'bundles/naptimejs/resources/productPrices.v3';
import ProductPricesV4 from 'bundles/naptimejs/resources/productPrices.v4';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import SubscriptionTrialsV1 from 'bundles/naptimejs/resources/subscriptionTrials.v1';
import type { ProductType as IProductType } from 'bundles/payments-common/common/ProductType';
import ProductType from 'bundles/payments-common/common/ProductType';
import { useGetProductPrices } from 'bundles/payments-common/hooks/useGetProductPrices';
import { useCart } from 'bundles/payments-common/utils/hooks';

export const usePromotion = (props: PropsToLandingPageProvider) => {
  const promoCode = props.landingPage?.promotionCode;
  const primary = props.landingPage?.primaryCourseraPlusProduct;
  const productId = primary ? getProductFromContentful(primary) : undefined;
  const {
    data: { promotion },
  } = useNaptime<PropsFromPromotion>(() => {
    if (!promoCode || !productId) {
      return {
        promotion: null,
      };
    }

    return {
      promotion: ProductPricesV4.getPromoForProductInfo(promoCode, productId),
    };
  }, [promoCode, productId]);

  return {
    promotion,
  };
};

type PricesInput = PropsToLandingPageProvider & PropsFromPromotion & PropsFromWithExistingCourseraLiteSubscription;

type PropsFromNaptime = {
  pricesWithCurrency?: SubscriptionTiersProductPrice[];
  prices?: SubscriptionTiersProductPrice[];
};

export const useGetSubscriptionTiersLandingPagePrices = (input: PricesInput) => {
  const { promotion, primaryId, secondaryId, existingCourseraLiteSubscription } = input;
  const { cart } = useCart({ cartId: existingCourseraLiteSubscription?.cartId });
  const currencyData = cart
    ? {
        currencyCode: cart?.currencyCode,
        countryIsoCode: cart?.countryIsoCode,
      }
    : undefined;

  const shouldFetchPriceByCurrency =
    currencyData?.currencyCode && existingCourseraLiteSubscription?.stripeSubscriptionPaymentInformation;

  const pricesWanted: SubscriptionTiersProductVariant[] = [];

  if (!promotion && primaryId) {
    pricesWanted.push(primaryId);
  }

  if (secondaryId) {
    pricesWanted.push(secondaryId);
  }

  const skip = pricesWanted.length === 0;

  const products = pricesWanted.map((productItemId) => {
    return {
      productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
      productItemId,
    };
  });

  const { productPrices: prices } = useGetProductPrices({ products, skip });

  const {
    data: { pricesWithCurrency },
  } = useNaptime<PropsFromNaptime>(() => {
    if (skip) {
      return {};
    }

    return {
      pricesWithCurrency: shouldFetchPriceByCurrency
        ? ProductPricesV3.getPricesByCurrencyCodeAndCountry(
            pricesWanted.map((productItemId) => {
              return {
                productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
                productItemId,
                ...currencyData,
              };
            })
          )
        : undefined,
    };
  }, [currencyData?.currencyCode, shouldFetchPriceByCurrency]);

  return {
    prices: pricesWithCurrency || prices,
  };
};

type PropsFromProductPrices = {
  courseraPlusMonthlyAndAnnualPrices?: SubscriptionTiersProductPrice[];
  courseraPlusMonthlyAndAnnualPricesByCurrency?: SubscriptionTiersProductPrice[];
};

export const useGetSubscriptionTiersProductPrices = ({
  skip = false,
  shouldGetCourseraPlusAnnual = true,
}: {
  skip?: boolean;
  shouldGetCourseraPlusAnnual?: boolean;
}) => {
  const { existingCourseraLiteSubscription: liteSubscription } = useExistingCourseraLiteSubscription(skip);
  const { cart } = useCart({ cartId: liteSubscription?.cartId });
  const currencyData = {
    countryIsoCode: cart?.countryIsoCode,
    currencyCode: cart?.currencyCode,
  };
  const shouldFetchPriceByCurrency =
    currencyData?.currencyCode && liteSubscription?.stripeSubscriptionPaymentInformation;

  const products: { productType: IProductType; productItemId: string }[] = [
    {
      productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
      productItemId: courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
    },
  ];

  if (shouldGetCourseraPlusAnnual) {
    products.push({
      productType: ProductType.COURSERA_PLUS_SUBSCRIPTION,
      productItemId: courseraPlusProductVariants.ANNUAL_SEVEN_DAY_FREE_TRIAL,
    });
  }

  const { productPrices: courseraPlusMonthlyAndAnnualProductPrices } = useGetProductPrices({ products, skip });

  const {
    data: { courseraPlusMonthlyAndAnnualPricesByCurrency },
  } = useNaptime<PropsFromProductPrices>(() => {
    if (skip) {
      return {};
    }

    const productListWithCurrencyCode = products.map((product) => ({ ...product, ...currencyData }));

    return {
      courseraPlusMonthlyAndAnnualPricesByCurrency: shouldFetchPriceByCurrency
        ? ProductPricesV3.getPricesByCurrencyCodeAndCountry(productListWithCurrencyCode)
        : undefined,
    };
  }, [liteSubscription?.[0]?.id, cart?.id, shouldFetchPriceByCurrency, products?.length]);

  // If the prices by specified currency are not available, fallback to the default prices/currency
  const allProductPrices = courseraPlusMonthlyAndAnnualPricesByCurrency ?? courseraPlusMonthlyAndAnnualProductPrices;

  return {
    courseraPlusMonthlyProductPrice: allProductPrices?.[0],
    ...(shouldGetCourseraPlusAnnual ? { courseraPlusAnnualProductPrice: allProductPrices?.[1] } : {}),
  };
};

export type PropsFromSubscriptionTiersFreeTrials = {
  subscriptionTrials: Array<SubscriptionTrialsV1>;
};

export const useSubscriptionTiersFreeTrials = (props: PropsToLandingPageProvider) => {
  const {
    data: { subscriptionTrials },
  } = useNaptime<PropsFromSubscriptionTiersFreeTrials>(() => {
    if (!user.isAuthenticatedUser() || !shouldGetFreeTrials(props)) {
      return { subscriptionTrials: [] };
    }
    const { COURSERA_PLUS_SUBSCRIPTION } = ProductType;
    const { COURSERA_PLUS_SUBSCRIPTION_UNDERLYING_PRODUCT_ITEM_ID } = constants;
    const userId = user.get().id;

    return {
      subscriptionTrials: SubscriptionTrialsV1.finder('findByUserAndUnderlyingProduct', {
        params: {
          userProductItemId: tupleToStringKey([
            userId,
            COURSERA_PLUS_SUBSCRIPTION,
            COURSERA_PLUS_SUBSCRIPTION_UNDERLYING_PRODUCT_ITEM_ID,
          ]),
        },
      }),
    };
  }, []);

  return {
    subscriptionTrials,
  };
};
